/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
} from '@msdyn365-commerce/core';
import { createOrderAsync } from '../../../data-actions/generated/DataActionExtension.g';
import {
    IBilling,
    IConsumer,
    ICourier,
    IDiscount,
    IDXC_CreateOrderResponse,
    IItem,
    IMerchant,
    IOrder,
    IShipping,
    IShippingamount,
    ITaxamount,
    ITotalamount } from '../../../data-actions/generated/DataServiceEntities.g';

export interface IAfterpayCreateOrderInput {
    totalAmount: ITotalamount;
    consumer: IConsumer;
    merchant: IMerchant;
    billing?: IBilling;
    shipping?: IShipping;
    items?: IItem[];
    discounts?: IDiscount[];
    merchantReference?: string;
    taxAmount?: ITaxamount;
    shippingAmount?: IShippingamount;
    courier?: ICourier;
    description?: string;
}

/**
 * AfterpayCreateOrderDataAction Input Action
 */
export class AfterpayCreateOrderDataActionInput implements IActionInput {
    public orderData: IOrder;
    public orderIdString: string;

    constructor (input: IAfterpayCreateOrderInput) {
        this.orderData = {
            token: undefined,
            expires: new Date(),
            totalAmount: input.totalAmount,
            consumer: input.consumer,
            merchant: input.merchant,
            billing: input.billing,
            shipping: input.shipping,
            items: input.items,
            discounts: input.discounts,
            merchantReference: input.merchantReference,
            taxAmount: input.taxAmount,
            shippingAmount: input.shippingAmount,
            courier: input.courier,
            description: input.description
        };

        this.orderIdString = this._getOrderIdString();
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `AfterpayCreateOrder ${this.orderIdString}`;
    public getCacheObjectType = () => 'AfterpayCreateOrder';
    public dataCacheType = (): CacheType => 'request';

    private _getOrderIdString(): string {
        let orderString = '';
        const o = this.orderData;

        if (o.consumer) {
            orderString += `${o.consumer.phoneNumber}${o.consumer.givenNames}${o.consumer.surname}${o.consumer.email}`;
        }

        if (o.billing) {
            orderString += `${o.billing.name}${o.billing.line1}${o.billing.state}${o.billing.postcode}${o.billing.countryCode}${o.billing.phoneNumber}`;
        }

        if (o.shipping) {
            orderString += `${o.shipping.name}${o.shipping.line1}${o.shipping.state}${o.shipping.postcode}${o.shipping.countryCode}${o.shipping.phoneNumber}`;
        }

        if (o.shippingAmount) {
            orderString += `${o.shippingAmount.amount}`;
        }

        return orderString;
    }
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: AfterpayCreateOrderDataActionInput): IActionInput => {
    return args;
    // return new AfterpayCreateOrderDataActionInput(JSON.parse('{"billing":null,"consumer":{"email":"wwojcik@dxc.com","givenNames":"WW","phoneNumber":"0410880949","surname":"ww"},
    // "discounts":null,"items":null,"merchant":{"redirectCancelUrl":"https:\\/\\/www.merchant.com\\/cancel","redirectConfirmUrl":"https:\\/\\/www.merchant.com\\/confirm"},
    // "merchantReference":"test12354","shipping":null,"shippingAmount":null,"taxAmount":null,"totalAmount":{"amount":"200","currency":"AUD"}}'));
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function createAfterpayOrderAction(
    input:AfterpayCreateOrderDataActionInput,
    ctx: IActionContext
):Promise<IDXC_CreateOrderResponse> {
    const result: IDXC_CreateOrderResponse = await createOrderAsync({ callerContext: ctx }, JSON.stringify(input.orderData));

    return Array.isArray(result) ? result[0] : result;
}

export const IAfterpayCreateOrderDataActionAction =  createObservableDataAction({
    action: <IAction<IDXC_CreateOrderResponse>>createAfterpayOrderAction,
    input: createInput
});
